import type { RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import { useTranslation } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import React from "react";

import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import Trans from "@/components/Trans/Trans";
import { useMeterColor } from "@/hooks";

import RulebookMeterEffects from "./RulebookMeterEffects";
import type { HudPanelMeter } from "./RulebookMeterPanel";
import useMeterMove from "./useMeterMove";

type ChipMeterProps = HudPanelMeter & { snapshot: RulebookDisplayedSnapshot; isArrived: boolean };

const ChipMeter = React.memo(({ meter, text, snapshot, isArrived }: ChipMeterProps) => {
  const meterColor = useMeterColor(meter.color);
  const move = useMeterMove({ meter, color: meterColor, snapshot, isArrived });
  return (
    <span
      className="relative flex items-center gap-1 whitespace-nowrap text-[11.5px] font-bold tabular-nums"
      data-testid={`RulebookHudChip-Meter-${meter.id}`}
    >
      <RulebookMeterEffects move={move} snapshotId={snapshot.messageId} meterId={meter.id} testIdPrefix="RulebookHudChip" />
      <span key={`icon-${move?.iconAnimation ? snapshot.messageId : "still"}`} className={`inline-flex ${move?.iconAnimation ?? ""}`}>
        <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={meterColor} size={IconSize.XSMALL} />
      </span>
      {text.mini}
    </span>
  );
});
ChipMeter.displayName = "ChipMeter";

type RulebookHudChipProps = {
  meters: HudPanelMeter[];
  snapshot: RulebookDisplayedSnapshot;
  isArrived: boolean;
  onOpenPanel: () => void;
  onExpand: () => void;
};

/** Minimized meters: a floating chip at the top right with each meter's icon and short value. */
const RulebookHudChip = ({ meters, snapshot, isArrived, onOpenPanel, onExpand }: RulebookHudChipProps) => {
  const { t } = useTranslation();
  return (
    <div data-testid="RulebookHudChip" className="relative z-[5] h-0 shrink-0">
      <div
        data-testid="RulebookHudChip-Body"
        className="absolute right-3 top-2 flex h-8 items-center gap-2.5 rounded-full border border-[#ACACAC] bg-gray-1 pl-3 pr-1 shadow-[0_6px_20px_rgba(0,0,0,0.25)] dark:border-gray-8 dark:bg-gray-3"
      >
        <button
          type="button"
          data-testid="RulebookHudChip-Open"
          title={t("chat:rulebook.hud.openSheet", { defaultValue: "See what moves these meters" })}
          onClick={onOpenPanel}
          className="flex h-full cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0"
        >
          {meters.map((entry) => (
            <ChipMeter key={entry.meter.id} {...entry} snapshot={snapshot} isArrived={isArrived} />
          ))}
          {snapshot.isPastState ? (
            <span data-testid="RulebookHudChip-PastStateMarker" className="whitespace-nowrap text-[10px] text-gray-10 dark:text-gray-11">
              <Trans i18nKey="chat:rulebook.hud.pastState" defaults="Past state" />
            </span>
          ) : null}
        </button>
        <button
          type="button"
          data-testid="RulebookHudChip-Expand"
          aria-label={t("chat:rulebook.hud.expand", { defaultValue: "Expand meters" })}
          title={t("chat:rulebook.hud.expand", { defaultValue: "Expand meters" })}
          onClick={onExpand}
          className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-gray-5 text-gray-11 hover:bg-gray-200 dark:bg-gray-4 dark:text-blue-11 dark:hover:bg-gray-5"
        >
          <Icon icon={IconVariants.CHEVRON_DOWN} size={IconSize.SMALL} />
        </button>
      </div>
    </div>
  );
};

export default React.memo(RulebookHudChip);
